'use client';

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';

import { cn } from '@repo/ui/lib/utils';

/**
 * Visual style shared by `TabsList`/`TabsTrigger`:
 * - `underline` (default): the original section-tab look — bottom border,
 *   bold active label.
 * - `segmented`: a pill-styled segmented control on a glass track (v2
 *   design system), used for compact single-choice controls like the
 *   period selector rather than full section navigation.
 */
type TabsVariant = 'underline' | 'segmented';

const TabsVariantContext = React.createContext<TabsVariant>('underline');
/** Current tab value, mirrored so segmented triggers know who owns the pill. */
const TabsValueContext = React.createContext<string | undefined>(undefined);
/** Optional restyle for the sliding pill (e.g. the view dock's mint chip). */
const TabsPillClassContext = React.createContext<string | undefined>(undefined);

function Tabs({
  className,
  value,
  defaultValue,
  onValueChange,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  // Mirror the active value for uncontrolled usage too — the segmented
  // pill animation needs to know the owner without Radix internals.
  const [internal, setInternal] = React.useState(defaultValue);
  const current = value ?? internal;

  return (
    <TabsValueContext.Provider value={current}>
      <TabsPrimitive.Root
        data-slot="tabs"
        className={cn('flex flex-col gap-2', className)}
        value={value}
        defaultValue={defaultValue}
        onValueChange={(next) => {
          setInternal(next);
          onValueChange?.(next);
        }}
        {...props}
      />
    </TabsValueContext.Provider>
  );
}

function TabsList({
  variant = 'underline',
  pillClassName,
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & {
  variant?: TabsVariant;
  pillClassName?: string;
}) {
  // Namespaces the sliding pill: several segmented groups on one page must
  // never animate a pill across each other.
  const groupId = React.useId();

  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPillClassContext.Provider value={pillClassName}>
        <LayoutGroup id={groupId}>
          <TabsPrimitive.List
            data-slot="tabs-list"
            data-variant={variant}
            className={cn(
              variant === 'underline' &&
                'after:bg-foreground/10 relative inline-flex items-end justify-start gap-8 pb-2 after:absolute after:right-0 after:bottom-2 after:left-0 after:h-[2px] after:content-[""]',
              variant === 'segmented' &&
                'bg-rowa-surface-control border-rowa-border-glass-highlight shadow-rowa-control rounded-rowa-full backdrop-blur-rowa-control inline-flex items-center gap-0.5 border p-1',
              className
            )}
            {...props}
          />
        </LayoutGroup>
      </TabsPillClassContext.Provider>
    </TabsVariantContext.Provider>
  );
}

function TabsTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = React.useContext(TabsVariantContext);
  const currentValue = React.useContext(TabsValueContext);
  const pillClassName = React.useContext(TabsPillClassContext);
  const reducedMotion = useReducedMotion();
  const isActivePill = variant === 'segmented' && currentValue === props.value;

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        variant === 'underline' &&
          'data-[state=active]:border-foreground data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground inline-flex items-center justify-center border-b-2 px-0 pb-2 text-sm font-medium whitespace-nowrap transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:font-bold data-[state=inactive]:border-transparent',
        variant === 'segmented' &&
          'text-rowa-label-md data-[state=active]:text-rowa-text-link data-[state=inactive]:text-rowa-text-secondary data-[state=inactive]:hover:text-rowa-text-primary focus-visible:ring-rowa-border-accent rounded-rowa-full relative inline-flex items-center justify-center px-3 py-1.5 whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    >
      {variant === 'segmented' ? (
        <>
          {isActivePill ? (
            <motion.span
              layoutId="segmented-pill"
              aria-hidden
              transition={
                reducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 40 }
              }
              className={cn(
                // The dock chip is the house selected treatment: mint glass
                // with a success-green outline behind link-tinted text.
                'bg-rowa-surface-dock-active border-rowa-status-success-border shadow-rowa-control rounded-rowa-full backdrop-blur-rowa-control absolute inset-0 border',
                pillClassName
              )}
            />
          ) : null}
          <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
        </>
      ) : (
        children
      )}
    </TabsPrimitive.Trigger>
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
